'use client';

import { Controller, FieldValues, Path, useFormContext } from 'react-hook-form';
import { FileUploadField, FileUploadFieldProps, UploadFieldFile } from '@/shared/ui/core/file-upload-field';


type ControlledFileUploadProps<T extends FieldValues> = Omit<
    FileUploadFieldProps,
    'files' | 'onFilesSelect' | 'onDeleteFile' | 'error' | 'disabled'
> & {
    name      : Path<T>;
    maxFiles ?: number;
}

const formatSize = (bytes: number) => {
    if (bytes < 1024) return `${ bytes } B`;
    if (bytes < 1024 * 1024) return `${ (bytes / 1024).toFixed(1) } KB`;
    return `${ (bytes / 1024 / 1024).toFixed(1) } MB`;
};

export const ControlledFileUpload = <T extends FieldValues>({
    name,
    maxFiles,
    multiple = true,
    ...props
}: ControlledFileUploadProps<T>) => {
    const { control, formState: { errors }, clearErrors } = useFormContext<T>();

    return (
        <Controller
            control={ control }
            name={ name }
            render={ ({ field }) => {
                const current: File[] = (field.value as File[] | undefined) ?? [];
                const isLimitReached  = maxFiles !== undefined && current.length >= maxFiles;

                const uiFiles: UploadFieldFile[] = current.map((file, index) => ({
                    id   : `${ file.name }-${ index }`,
                    name : file.name,
                    size : formatSize(file.size),
                    state: 'success',
                }));

                const handleSelect = (list: FileList | null) => {
                    if (!list) return;

                    const incoming = Array.from(list);
                    const next     = maxFiles !== undefined
                        ? [ ...current, ...incoming ].slice(0, maxFiles)
                        : [ ...current, ...incoming ];

                    field.onChange(next);
                    clearErrors(name);
                };

                const handleDelete = (fileId: string) => {
                    const next = current.filter((file, index) => `${ file.name }-${ index }` !== fileId);
                    field.onChange(next);
                    clearErrors(name);
                };

                return (
                    <FileUploadField
                        { ...props }
                        multiple={ multiple }
                        disabled={ isLimitReached }
                        files={ uiFiles }
                        onFilesSelect={ handleSelect }
                        onDeleteFile={ handleDelete }
                        error={ errors[name]?.message as string | undefined }
                    />
                );
            } }
        />
    );
};
